import { motion } from "framer-motion";
import React from "react";
import {
  FiArrowLeft,
  FiClock,
  FiMapPin,
  FiPhone,
  FiShare2,
} from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import AnimatedWrapper from "../components/AnimatedWrapper";
import GalleryCarousel from "../components/GalleryCarousel";
import GalleryMenu from "../components/GalleryMenu";
import MapEmbed from "../components/MapEmbed";
import RatingStars from "../components/RatingStars";
import UmkmCard from "../components/UmkmCard";
import umkmData from "../data/umkm.json";
import { Umkm } from "../types/umkm";

const DetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const umkm = (umkmData as Umkm[]).find(
    (item) => item.id === parseInt(id || "0")
  );

  if (!umkm) {
    return (
      <AnimatedWrapper>
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
          <div className="text-center bg-white rounded-2xl shadow-soft p-12 border border-slate-100/50">
            <h1 className="text-3xl font-bold text-slate-900 mb-6">
              UMKM tidak ditemukan
            </h1>
            <Link
              to="/"
              className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300"
            >
              Kembali ke beranda
            </Link>
          </div>
        </div>
      </AnimatedWrapper>
    );
  }

  const shareUrl = () => {
    if (navigator.share) {
      navigator.share({
        title: umkm.name,
        text: umkm.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link telah disalin ke clipboard!");
    }
  };

  return (
    <AnimatedWrapper>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100/50 pt-20">
        {/* Header */}
        <div className="bg-white shadow-soft border-b border-slate-100/50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center">
              <Link
                to="/"
                aria-label="Kembali ke halaman utama"
                className="flex items-center text-slate-600 hover:text-primary-600 transition-colors duration-300"
              >
                <FiArrowLeft className="h-5 w-5 mr-2" />
                <span className="font-medium">Kembali</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-12">
          {/* Two-Column Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Left Column - Visual Elements (2/3 width on large screens) */}
            <div className="lg:col-span-2 space-y-8 lg:space-y-12">
              {/* Galeri Tempat Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="bg-white rounded-2xl shadow-soft border border-slate-100/50 p-6 lg:p-8"
              >
                <h3 className="font-bold text-xl lg:text-3xl text-slate-900 mb-8">
                  Galeri Tempat
                </h3>
                {umkm.placeGallery && umkm.placeGallery.length > 0 ? (
                  <GalleryCarousel
                    images={umkm.placeGallery}
                    businessName={umkm.name}
                  />
                ) : (
                  <div className="bg-slate-50 rounded-xl p-12 text-center border border-slate-100/50">
                    <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <p className="text-slate-600 font-medium">
                      Tidak ada foto tempat tersedia
                    </p>
                    <p className="text-slate-500 text-sm mt-2">
                      Foto akan ditambahkan segera
                    </p>
                  </div>
                )}
              </motion.div>

              {/* Galeri Menu Section */}
              {umkm.menuGallery && umkm.menuGallery.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="bg-white rounded-2xl shadow-soft border border-slate-100/50 p-6 lg:p-8"
                >
                  <h3 className="font-bold text-xl lg:text-3xl text-slate-900 mb-8">
                    Galeri Menu
                  </h3>
                  <GalleryMenu
                    images={umkm.menuGallery}
                    businessName={umkm.name}
                  />
                </motion.div>
              )}

              {/* Map Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="bg-white rounded-2xl shadow-soft border border-slate-100/50 p-6 lg:p-8"
              >
                <div className="rounded-xl overflow-hidden">
                  <MapEmbed
                    embedUrl={umkm.googleMapsEmbedUrl}
                    address={umkm.address}
                    businessName={umkm.name}
                  />
                </div>
              </motion.div>
            </div>

            {/* Right Column - Textual Content & Actions (1/3 width, sticky on large screens) */}
            <div className="lg:col-span-1 lg:sticky lg:top-24 lg:self-start space-y-6">
              {/* Basic Info Block */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="bg-white rounded-2xl shadow-soft border border-slate-100/50 p-6"
              >
                {/* Category Badge */}
                <div className="mb-4">
                  <span className="bg-primary-100 text-primary-800 rounded-full px-4 py-2 text-sm font-medium">
                    {umkm.category}
                  </span>
                  {umkm.isFeatured && (
                    <span className="bg-amber-100 text-amber-800 rounded-full px-3 py-1 text-sm font-medium ml-2">
                      Featured
                    </span>
                  )}
                </div>

                {/* Name */}
                <h1 className="font-bold text-3xl lg:text-4xl xl:text-5xl text-slate-900 mb-6 leading-tight">
                  {umkm.name}
                </h1>

                {/* Rating */}
                <div className="mb-4">
                  <RatingStars rating={umkm.rating} size="md" />
                </div>

                {/* Status */}
                <div className="mb-4">
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      umkm.isOpen
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    <FiClock className="h-4 w-4 mr-1" />
                    {umkm.isOpen ? "Buka" : "Tutup"}
                  </span>
                </div>

                {/* Address */}
                <div className="flex items-start text-slate-600">
                  <FiMapPin className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm leading-relaxed">
                    {umkm.address}
                  </span>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="space-y-3"
              >
                {/* Hubungi Button */}
                <motion.button
                  whileHover={{
                    scale: 1.02,
                    boxShadow: "0 8px 20px -5px rgba(16, 185, 129, 0.3)",
                  }}
                  whileTap={{
                    scale: 0.98,
                    boxShadow: "0 2px 10px -3px rgba(16, 185, 129, 0.2)",
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 20,
                    boxShadow: { duration: 0.2 },
                  }}
                  className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white px-6 py-3.5 rounded-xl font-medium shadow-md transition-all duration-300 relative overflow-hidden group"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FiPhone className="h-4 w-4" />
                  </motion.div>
                  <span className="relative z-10">Hubungi</span>
                </motion.button>

                {/* Bagikan Button */}
                <motion.button
                  onClick={shareUrl}
                  aria-label="Bagikan halaman ini"
                  whileHover={{
                    scale: 1.02,
                    boxShadow: "0 8px 20px -5px rgba(0, 0, 0, 0.1)",
                  }}
                  whileTap={{
                    scale: 0.98,
                    boxShadow: "0 2px 10px -3px rgba(0, 0, 0, 0.1)",
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 20,
                    boxShadow: { duration: 0.2 },
                  }}
                  className="w-full flex items-center justify-center space-x-2 bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-800 px-6 py-3.5 rounded-xl font-medium border border-slate-200 hover:border-slate-300 transition-all duration-300 relative overflow-hidden group"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FiShare2 className="h-4 w-4" />
                  </motion.div>
                  <span className="relative z-10">Bagikan</span>
                  <motion.div
                    className="absolute inset-0 bg-slate-50"
                    initial={{ scale: 0, opacity: 0 }}
                    whileHover={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                </motion.button>
              </motion.div>

              {/* About Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="bg-white rounded-2xl shadow-soft border border-slate-100/50 p-6"
              >
                <h3 className="font-semibold text-lg text-slate-900 mb-4">
                  Tentang
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  {umkm.description}
                </p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {umkm.story}
                </p>
              </motion.div>

              {/* Facilities Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                className="bg-white rounded-2xl shadow-soft border border-slate-100/50 p-6"
              >
                <h3 className="font-semibold text-lg text-slate-900 mb-4">
                  Fasilitas
                </h3>
                <div className="flex flex-wrap gap-2">
                  {umkm.facilities.map((facility, index) => (
                    <span
                      key={index}
                      className="bg-slate-100/80 text-slate-700 px-3 py-1 rounded-lg text-xs font-medium border border-slate-200/50"
                    >
                      {facility}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Operational Hours Section */}
              {umkm.operationalHours && umkm.operationalHours.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.45 }}
                  className="bg-white rounded-2xl shadow-soft border border-slate-100/50 p-6"
                >
                  <h3 className="font-poppins font-semibold text-lg text-slate-900 mb-3">
                    Jam Operasional
                  </h3>
                  <div className="space-y-2">
                    {umkm.operationalHours.map((opHour, index) => (
                      <div
                        key={index}
                        className="flex justify-between text-sm mb-1"
                      >
                        <span className="text-slate-600">{opHour.day}</span>
                        <span
                          className={`font-medium ${
                            opHour.isOpen === false
                              ? "text-red-500"
                              : "text-slate-800"
                          }`}
                        >
                          {opHour.hours}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Products/Services Section */}
              {umkm.products && umkm.products.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.55 }}
                  className="bg-white rounded-2xl shadow-soft border border-slate-100/50 p-6"
                >
                  <h3 className="font-semibold text-lg text-slate-900 mb-4">
                    Produk & Layanan
                  </h3>
                  <div className="space-y-3">
                    {umkm.products.map((product, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-100"
                      >
                        <span className="font-medium text-slate-900 text-sm">
                          {product.name}
                        </span>
                        <span className="font-semibold text-primary-600 text-sm">
                          Rp {product.price.toLocaleString("id-ID")}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          {/* Related UMKM Section - Full Width Outside Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.6 }}
            className="mt-12 lg:mt-16"
          >
            <h3 className="font-semibold text-2xl lg:text-3xl text-slate-900 mb-8">
              UMKM Serupa
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(umkmData as Umkm[])
                .filter(
                  (item) =>
                    item.category === umkm.category && item.id !== umkm.id
                )
                .slice(0, 3)
                .map((relatedUmkm) => (
                  <UmkmCard key={relatedUmkm.id} umkm={relatedUmkm} />
                ))}
            </div>
            {(umkmData as Umkm[]).filter(
              (item) => item.category === umkm.category && item.id !== umkm.id
            ).length === 0 && (
              <div className="text-center py-12 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-slate-500 text-lg">
                  Tidak ada UMKM serupa yang ditemukan
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </AnimatedWrapper>
  );
};

export default DetailPage;